import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';
import { Link } from 'react-router-dom';
import { Shield, FileText, AlertTriangle, Scale } from 'lucide-react';
import { CURRENT_SELLER_TERMS_VERSION } from '@/hooks/useTermsAcceptance';

interface SellerAgreementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAccepted: () => void;
}

export function SellerAgreementModal({
  open,
  onOpenChange,
  onAccepted,
}: SellerAgreementModalProps) {
  const [accepted, setAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccept = async () => {
    if (!accepted) return;

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('profiles')
        .update({
          terms_accepted_at: new Date().toISOString(),
          terms_version: CURRENT_SELLER_TERMS_VERSION,
        })
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success('Seller agreement accepted');
      onAccepted();
      onOpenChange(false);
    } catch (error) {
      console.error('Error accepting seller agreement:', error);
      toast.error('Failed to accept agreement. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg sm:w-full">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-primary" />
            Seller Agreement
          </DialogTitle>
          <DialogDescription>
            Please review and accept our seller agreement before listing items for sale.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-64 rounded-md border border-white/10 bg-white/[0.02] p-4">
          <div className="space-y-4 text-sm text-muted-foreground">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 mt-0.5 text-primary shrink-0" />
              <div>
                <h4 className="font-medium text-foreground mb-1">Accurate Listings</h4>
                <p>
                  You agree to provide accurate descriptions, conditions, and photos of all items you list. 
                  Misrepresentation may result in account suspension.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 mt-0.5 text-warning shrink-0" />
              <div>
                <h4 className="font-medium text-foreground mb-1">Prohibited Items</h4>
                <p>
                  You may not list counterfeit, stolen, or otherwise illegally obtained items. 
                  Violations will be reported to authorities and may result in permanent ban.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 mt-0.5 text-success shrink-0" />
              <div>
                <h4 className="font-medium text-foreground mb-1">Identity Disclosure</h4>
                <p>
                  If you complete identity verification, you consent to having your verified name 
                  disclosed to buyers upon request for transaction accountability.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 mt-0.5 text-info shrink-0" />
              <div>
                <h4 className="font-medium text-foreground mb-1">Peer-to-Peer Responsibility</h4>
                <p>
                  All transactions are directly between you and the buyer. CardSimp does not handle 
                  payments, shipping, or disputes. You are solely responsible for fulfilling sales.
                </p>
              </div>
            </div>

            <div className="border-t border-white/10 pt-4 mt-4">
              <p className="text-xs">
                This agreement supplements our{' '}
                <Link to="/terms" className="text-primary hover:underline" target="_blank">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/disclaimer" className="text-primary hover:underline" target="_blank">
                  Marketplace Disclaimer
                </Link>
                .
              </p>
            </div>
          </div>
        </ScrollArea>

        <div className="flex items-start gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/10">
          <Checkbox
            id="accept-seller-terms"
            checked={accepted}
            onCheckedChange={(checked) => setAccepted(checked === true)}
          />
          <Label htmlFor="accept-seller-terms" className="text-sm leading-relaxed cursor-pointer">
            I agree to the seller obligations and understand I am solely responsible for my listings 
            and transactions on this platform.
          </Label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAccept} disabled={!accepted || isSubmitting}>
            {isSubmitting ? 'Accepting...' : 'Accept & List'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Keep legacy export for backwards compatibility
export { SellerAgreementModal as TermsAcceptanceModal };
